// Service layer del rol nutricionista (Sprint 5 T11 — workflow catalogo + Δ3).
// Los tipos siguen 1:1 los schemas Pydantic en backend/app/schemas/nutricionista.py
// (PR T8 + integracion Lote 3+4). Si el contrato cambia, ajustar aqui.
import { api } from '../lib/api'

// ============================================================
// Workflow polimorfico (ADR-007 + Δ3)
// ============================================================
export type EstadoWorkflow =
  | 'BORRADOR'
  | 'PENDIENTE'
  | 'APROBADO'
  | 'BLOQUEADO'

export interface WorkflowTransicionResponse {
  entidad_tipo: string
  entidad_id: number
  estado_anterior: string | null
  estado_nuevo: string
  workflow_id: number
  auto_aprobado: boolean
  payload: Record<string, unknown> | null
}

export interface EnviarAprobacionBody {
  notas?: string | null
}

export interface AutoAprobarBody {
  // Min 10 chars segun schema backend.
  justificacion: string
}

export interface BloquearBody {
  // Min 5 chars segun schema backend.
  motivo: string
}

// S8 T27: BLOQUEADO -> APROBADO. Motivo min 10 chars (mas estricto que Bloquear).
export interface DesbloquearBody {
  motivo: string
}

// ============================================================
// INGREDIENTES (alimentos)
// ============================================================
export interface IngredienteResponse {
  id: number
  codigo: string
  nombre: string
  unidad_default: string | null
  es_alergeno: boolean
  tipo_alergeno: string | null
  categoria: string | null
  activo: boolean
  estado_workflow: EstadoWorkflow | string
  // Composicion INCAP (Sprint 8 final - ahora persisten)
  kcal?: number | null
  proteina_g?: number | null
  cho_g?: number | null
  grasa_g?: number | null
  sodio_mg?: number | null
  potasio_mg?: number | null
  fibra_g?: number | null
  fuente_composicion?: string | null
}

export interface IngredienteCreateBody {
  codigo: string
  nombre: string
  unidad_default?: string | null
  es_alergeno?: boolean
  tipo_alergeno?: string | null
  categoria?: string | null
  activo?: boolean
  // Composicion INCAP - opcionales, persistidos en backend
  kcal?: number | null
  proteina_g?: number | null
  cho_g?: number | null
  grasa_g?: number | null
  sodio_mg?: number | null
  potasio_mg?: number | null
  fibra_g?: number | null
}

export interface IngredienteUpdateBody {
  nombre?: string | null
  unidad_default?: string | null
  es_alergeno?: boolean | null
  tipo_alergeno?: string | null
  categoria?: string | null
  activo?: boolean | null
  // Composicion INCAP - editables tambien
  kcal?: number | null
  proteina_g?: number | null
  cho_g?: number | null
  grasa_g?: number | null
  sodio_mg?: number | null
  potasio_mg?: number | null
  fibra_g?: number | null
}

export interface ListarIngredientesParams {
  categoria?: string | null
  grupo?: string | null
  page?: number
  limit?: number
}

// ============================================================
// PLATO TEMPLATE
// ============================================================
export interface PlatoTemplateResponse {
  id: number
  codigo: string
  nombre_compuesto: string
  plato_id_legacy: number | null
  categoria_preparacion_id: number | null
  descripcion: string | null
  estado: string
  version: number
  activo: boolean
}

export interface PlatoBomItemCreate {
  ingrediente_id: number
  cantidad: number
  unidad: string
}

export interface PlatoTemplateCreateBody {
  codigo: string
  nombre_compuesto: string
  plato_id_legacy?: number | null
  categoria_preparacion_id?: number | null
  descripcion?: string | null
  activo?: boolean
  // Sprint 8 T25: BOM se persiste al crear (junto con Plato espejo legacy).
  bom?: PlatoBomItemCreate[]
}

// Sprint 8 T25: respuesta de GET /platos-template/{id}/bom (read-only viewer).
export interface PlatoBomItemResponse {
  ingrediente_id: number
  ingrediente_codigo: string
  ingrediente_nombre: string
  cantidad: number
  unidad_codigo: string
  unidad_abreviatura: string
  cantidad_en_gramos_calc: number | null
  orden: number
}

export interface PlatoTemplateUpdateBody {
  nombre_compuesto?: string | null
  plato_id_legacy?: number | null
  categoria_preparacion_id?: number | null
  descripcion?: string | null
  activo?: boolean | null
  // Sprint 8 final: editar BOM tras crear. Si viene, reemplaza completo.
  bom?: Array<{ ingrediente_id: number; cantidad: number; unidad: string }>
}

// BOM (lista de ingredientes con cantidad+unidad). El endpoint formal de
// receta_ingrediente no esta en T8, lo mantenemos como tipo cliente para
// que el form lo capture y se envie cuando el endpoint exista.
export interface BomItem {
  ingrediente_id: number
  ingrediente_codigo?: string
  ingrediente_nombre?: string
  cantidad: number
  unidad: string
}

// ============================================================
// DIETAS
// ============================================================
export interface DietaResponse {
  id: number
  codigo: string
  nombre: string
  calorias: number | null
  cho_pct: number | null
  prot_pct: number | null
  grasa_pct: number | null
  menu_referencia: string | null
  observaciones: string | null
  activo: boolean
}

export interface DietaUpdateBody {
  // S8 T26: macros editables (antes solo observaciones/activo).
  calorias?: number | null
  cho_pct?: number | null
  prot_pct?: number | null
  grasa_pct?: number | null
  observaciones?: string | null
  menu_referencia?: string | null
  activo?: boolean | null
}

export interface DietaCreateBody {
  // Sprint 8: backend normaliza codigo+nombre a MAYUSCULAS automaticamente.
  codigo: string
  nombre: string
  calorias?: number | null
  cho_pct?: number | null
  prot_pct?: number | null
  grasa_pct?: number | null
  menu_referencia?: string | null
  observaciones?: string | null
  activo?: boolean
}

// ============================================================
// MENUS
// ============================================================
export interface MenuResponse {
  id: number
  codigo: string
  nombre: string
  fecha_vigencia_inicio: string // ISO date
  fecha_vigencia_fin: string | null
  estado: 'borrador' | 'vigente' | 'archivado' | string
  notas: string | null
}

export interface MenuCreateBody {
  codigo: string
  nombre: string
  fecha_vigencia_inicio: string // ISO date YYYY-MM-DD
  fecha_vigencia_fin?: string | null
  notas?: string | null
}

export interface AsignarPlatoItem {
  dieta_id: number
  tiempo_id: number
  dia_semana: number // 1..7
  plato_id: number
  orden_plato?: number
  cantidad?: string | null
  // S8 T42 - opcion de menu (NULL = sin agrupar, backwards compatible)
  menu_opcion_id?: number | null
}

export interface AsignarPlatosBody {
  items: AsignarPlatoItem[]
}

// Sprint 8 T24 - vista calendario: matriz DTP de un menu.
export interface DtpMenuItem {
  id: number
  dieta_id: number
  dieta_codigo: string
  tiempo_id: number
  dia_semana: number
  plato_id: number
  plato_codigo: string
  plato_template_codigo: string | null
  plato_nombre: string
  orden_plato: number
  cantidad: string | null
  // S8 T42 - agrupacion por opcion de menu
  menu_opcion_id: number | null
}

// S8 T42 - opciones de menu
export interface MenuOpcionResponse {
  id: number
  menu_id: number
  dieta_id: number
  tiempo_id: number
  dia_semana: number
  nombre: string
  descripcion: string | null
  orden: number
  activo: boolean
}

export interface MenuOpcionCreateBody {
  dieta_id: number
  tiempo_id: number
  dia_semana: number
  nombre: string
  descripcion?: string | null
  orden?: number
}

export interface MenuOpcionUpdateBody {
  nombre?: string | null
  descripcion?: string | null
  orden?: number | null
  activo?: boolean | null
}

// ============================================================
// CATALOGO unidades de medida (Sprint 8 T22)
// ============================================================
export interface UnidadMedidaResponse {
  id: number
  codigo: string
  nombre: string
  abreviatura: string
  factor_a_gramos: number | null
  factor_a_ml: number | null
  tipo: 'PESO' | 'VOLUMEN' | 'UNIDAD' | 'PORCION' | string
  activa: boolean
}

// ============================================================
// CATALOGO categorias de alimento (Sprint 8 T23)
// ============================================================
export interface CategoriaAlimentoResponse {
  id: number
  codigo: string
  nombre: string
  descripcion: string | null
  activa: boolean
}

// ============================================================
// REPORTE auto-aprobaciones (admin)
// ============================================================
export interface AutoAprobacionItem {
  id: number
  entidad_tipo: string
  entidad_id: number
  estado: string
  revisor_id: number | null
  aprobador_id: number | null
  fecha_evento: string
  motivo: string | null
  payload: Record<string, unknown> | null
}

// ============================================================
// API client
// ============================================================
export const nutricionistaApi = {
  // ----- Ingredientes -----
  listarIngredientes: (params: ListarIngredientesParams = {}): Promise<IngredienteResponse[]> =>
    api.get('/nutricionista/ingredientes', { params }).then((r) => r.data),
  crearIngrediente: (body: IngredienteCreateBody): Promise<IngredienteResponse> =>
    api.post('/nutricionista/ingredientes', body).then((r) => r.data),
  actualizarIngrediente: (id: number, body: IngredienteUpdateBody): Promise<IngredienteResponse> =>
    api.put(`/nutricionista/ingredientes/${id}`, body).then((r) => r.data),
  enviarAprobacionIngrediente: (id: number, body: EnviarAprobacionBody = {}): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/ingredientes/${id}/enviar-aprobacion`, body).then((r) => r.data),
  autoAprobarIngrediente: (id: number, body: AutoAprobarBody): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/ingredientes/${id}/auto-aprobar`, body).then((r) => r.data),
  bloquearIngrediente: (id: number, body: BloquearBody): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/ingredientes/${id}/bloquear`, body).then((r) => r.data),
  desbloquearIngrediente: (id: number, body: DesbloquearBody): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/ingredientes/${id}/desbloquear`, body).then((r) => r.data),

  // ----- Platos template -----
  listarPlatos: (page = 1, limit = 50): Promise<PlatoTemplateResponse[]> =>
    api.get('/nutricionista/platos-template', { params: { page, limit } }).then((r) => r.data),
  crearPlato: (body: PlatoTemplateCreateBody): Promise<PlatoTemplateResponse> =>
    api.post('/nutricionista/platos-template', body).then((r) => r.data),
  actualizarPlato: (id: number, body: PlatoTemplateUpdateBody): Promise<PlatoTemplateResponse> =>
    api.put(`/nutricionista/platos-template/${id}`, body).then((r) => r.data),
  obtenerBomPlato: (id: number): Promise<PlatoBomItemResponse[]> =>
    api.get(`/nutricionista/platos-template/${id}/bom`).then((r) => r.data),
  enviarAprobacionPlato: (id: number, body: EnviarAprobacionBody = {}): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/platos-template/${id}/enviar-aprobacion`, body).then((r) => r.data),
  autoAprobarPlato: (id: number, body: AutoAprobarBody): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/platos-template/${id}/auto-aprobar`, body).then((r) => r.data),
  bloquearPlato: (id: number, body: BloquearBody): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/platos-template/${id}/bloquear`, body).then((r) => r.data),
  desbloquearPlato: (id: number, body: DesbloquearBody): Promise<WorkflowTransicionResponse> =>
    api.post(`/nutricionista/platos-template/${id}/desbloquear`, body).then((r) => r.data),

  // ----- Dietas -----
  listarDietas: (): Promise<DietaResponse[]> =>
    api.get('/nutricionista/dietas').then((r) => r.data),
  crearDieta: (body: DietaCreateBody): Promise<DietaResponse> =>
    api.post('/nutricionista/dietas', body).then((r) => r.data),
  actualizarDieta: (id: number, body: DietaUpdateBody): Promise<DietaResponse> =>
    api.put(`/nutricionista/dietas/${id}`, body).then((r) => r.data),

  // ----- Menus -----
  listarMenus: (): Promise<MenuResponse[]> =>
    api.get('/nutricionista/menus').then((r) => r.data),
  crearMenu: (body: MenuCreateBody): Promise<MenuResponse> =>
    api.post('/nutricionista/menus', body).then((r) => r.data),
  asignarPlatosMenu: (menuId: number, body: AsignarPlatosBody): Promise<{ insertados: number }> =>
    api.post(`/nutricionista/menus/${menuId}/asignar-platos`, body).then((r) => r.data),
  // S8 T41: elimina un DTP puntual (1 plato de una celda multi-plato)
  eliminarDtpMenu: (menuId: number, dtpId: number): Promise<void> =>
    api.delete(`/nutricionista/menus/${menuId}/dtp/${dtpId}`).then(() => undefined),
  listarDtpsMenu: (menuId: number, dietaId?: number): Promise<DtpMenuItem[]> =>
    api
      .get(`/nutricionista/menus/${menuId}/dtps`, {
        params: dietaId ? { dieta_id: dietaId } : {},
      })
      .then((r) => r.data),

  // S8 T42 - CRUD opciones de menu
  listarOpcionesMenu: (
    menuId: number,
    params: { dieta_id?: number; tiempo_id?: number; dia_semana?: number } = {},
  ): Promise<MenuOpcionResponse[]> =>
    api
      .get(`/nutricionista/menus/${menuId}/opciones`, { params })
      .then((r) => r.data),
  crearOpcionMenu: (menuId: number, body: MenuOpcionCreateBody): Promise<MenuOpcionResponse> =>
    api.post(`/nutricionista/menus/${menuId}/opciones`, body).then((r) => r.data),
  actualizarOpcionMenu: (
    menuId: number,
    opId: number,
    body: MenuOpcionUpdateBody,
  ): Promise<MenuOpcionResponse> =>
    api.put(`/nutricionista/menus/${menuId}/opciones/${opId}`, body).then((r) => r.data),
  eliminarOpcionMenu: (menuId: number, opId: number): Promise<void> =>
    api.delete(`/nutricionista/menus/${menuId}/opciones/${opId}`).then(() => undefined),
  activarMenu: (menuId: number): Promise<MenuResponse> =>
    api.post(`/nutricionista/menus/${menuId}/activar`).then((r) => r.data),
  archivarMenu: (menuId: number): Promise<MenuResponse> =>
    api.post(`/nutricionista/menus/${menuId}/archivar`).then((r) => r.data),

  // ----- Admin: auto-aprobaciones (reporte mensual Δ3) -----
  listarAutoAprobaciones: (): Promise<AutoAprobacionItem[]> =>
    api.get('/admin/auto-aprobaciones').then((r) => r.data),

  // ----- Catalogo unidades de medida (Sprint 8 T22) -----
  listarUnidadesMedida: (): Promise<UnidadMedidaResponse[]> =>
    api.get('/catalogo/unidades-medida').then((r) => r.data),

  // ----- Catalogo categorias de alimento (Sprint 8 T23) -----
  listarCategoriasAlimento: (): Promise<CategoriaAlimentoResponse[]> =>
    api.get('/catalogo/categorias-alimento').then((r) => r.data),
}
